// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import Overlay from '../Overlay';
import Form from '../Form';
import Header from './Header';
import ScrollDirection from './ScrollDirection';

import '../../helpers/base.less';
import './Drawer.less';

/**
 * `Drawer` component.
 */
const Drawer = ({ baseClassName, className, children, title, subtitle, backButton, onClose, form, ...props }) => {
    if (form) {
        form.footerClassName = classNames(form.footerClassName, `${baseClassName}__form-footer`);
        form.className = classNames(`${baseClassName}__form`, form.className);
        form.cancelButton = {
            onClick: onClose,
            ...form.cancelButton,
        };
        children = <Form {...form}>{children}</Form>;
    } else {
        children = <div className={`${baseClassName}__inner-body`}>{children}</div>;
    }

    return (
        <Overlay
            className={classNames(
                baseClassName,
                {
                    [`${baseClassName}--form`]: form,
                },
                className
            )}
            onClose={onClose}
            canCloseOnBackdropClick={false}
            {...props}
        >
            <Header backButton={backButton} onClose={onClose}>{title}</Header>
            <ScrollDirection>
                {({ direction, handleScroll }) => (
                    <div className={`${baseClassName}__body`} onScroll={handleScroll}>
                        <div>{/* workaround for position: sticky in safari */}
                            {subtitle && (
                                <div
                                    className={classNames(`${baseClassName}__subheader`, {
                                        [`${baseClassName}__subheader--hidden`]: direction === 'down',
                                    })}
                                >
                                    <h4 className={`${baseClassName}__subheader-title`}>{subtitle}</h4>
                                </div>
                            )}
                            {children}
                        </div>
                    </div>
                )}
            </ScrollDirection>
        </Overlay>
    );
};

Drawer.propTypes = {
    /**
     * Toggles visibility.
     */
    isOpen: PropTypes.bool,
    /**
     * The title of the `Drawer`.
     */
    title: PropTypes.node.isRequired,
    /**
     * The subtitle of the `Drawer`.
     */
    subtitle: PropTypes.node,
    /**
     * Show back button in the header. If true the close button is not show.
     */
    backButton: PropTypes.bool,
    /**
     * Overlay placement.
     */
    placement: PropTypes.oneOf(['left', 'right']),
    /**
     * Overlay size.
     */
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
    /**
     * onClose handler.
     */
    onClose: PropTypes.func,
    /**
     * Wraps children into [Form](#!/Form) if not empty.
     */
    form: PropTypes.object,
    /**
     * Content of the `Drawer`.
     */
    children: PropTypes.node,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};

Drawer.defaultProps = {
    isOpen: false,
    subtitle: '',
    backButton: false,
    placement: 'left',
    size: 'md',
    onClose: null,
    form: null,
    children: null,
    className: null,
    baseClassName: `${CLS_PREFIX}drawer`,
};

export default Drawer;



// WEBPACK FOOTER //
// ./components/Drawer/Drawer.js